import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MocksService } from '../../app-commons/mocks/mocks.service';
import { map, mergeMap, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { GateReadingsService } from '../gate-readings/gate-readings.service';
import { ResultsService } from '../../season/results/results.service';
import {
  BehaviorSubject,
  from,
  merge,
  Observable,
  ReplaySubject,
  Subject,
  Subscription
} from 'rxjs';
import { Driver } from '../drivers/driver';
import { Result } from '../../season/results/result/result';
import { RankingsService } from '../../season/rankings/rankings.service';
import { Ranking } from '../../season/rankings/ranking';
import { RacesService } from '../../season/races/races.service';
import { RaceStandingsService } from '../../season/standings/race-standings.service';
import { DriversService } from '../drivers/drivers.service';
import { ResultsImportService } from './results-import.service';
import { RemoteResultsService } from '../../season/results/remote-results.service';
import { RfidSolutionsTagArriveReadingsService } from '../tag-readings/rfid-solutions-tag-arrive-readings.service';
import { TagReading } from '../tag-readings/tag-reading';
import { TagReadingsService } from '../tag-readings/tag-readings.service';
import { RacePlan } from '../../season/race-plan/race-plan';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'mc-results-import',
  templateUrl: './results-import.component.html',
  styleUrls: ['./results-import.component.scss']
})
export class ResultsImportComponent implements OnInit, OnDestroy, AfterViewInit {

  readonly RFID_ACCURACY_THRESHOLD = 2;

  racePlan: RacePlan;
  rankings: Ranking[] = []; // Rankings of the race.

  tagReadings: TagReading[] = [];

  drivers$ = new ReplaySubject<Driver[]>(1);
  driversFromCsv$ = new Subject<Driver[]>();

  tagReadings$ = new BehaviorSubject<TagReading[]>([]);
  remoteRawTagReadingsWatcher$: Observable<string>;
  private remoteRawTagReadingsWatcherSubscription: Subscription;

  resolveSubscription: Subscription;
  resultSubscription: Subscription;
  dbConnectionSubscription: Subscription;
  syncSubscription: Subscription;

  jsonExport = null;

  tagReadingsURL = environment.tagReadingsURL || '';

  resultsFromTagReadings$: Observable<Result[]> = this.drivers$.pipe(
    switchMap((drivers: Driver[]) => this.tagReadings$.pipe(
      tap((tagReadings: TagReading[]) => this.tagReadings = tagReadings),
      mergeMap((tagReadings: TagReading[]) => from(tagReadings).pipe(
        map((tagReading: TagReading) => [tagReading, this.RFID_ACCURACY_THRESHOLD]),
        this.tagReadingsService.filterThresholdOperator(),
        map(tagReading => [tagReading, drivers]),
        this.tagReadingsService.mapToResultsOperator(),
        toArray()
      ))
    ))
  );

  constructor(
    private readonly http: HttpClient,
    private readonly mocks: MocksService,
    private readonly gateReadingsService: GateReadingsService,
    private readonly tagReadingsService: TagReadingsService,
    public readonly resultsService: ResultsService,
    private readonly rankingsService: RankingsService,
    private readonly racesService: RacesService,
    private readonly standingsService: RaceStandingsService,
    private readonly driversService: DriversService,
    private readonly resultsImportService: ResultsImportService,
    public readonly remoteResultsService: RemoteResultsService,
    private readonly rfidSolutionsTagArriveReadingsService: RfidSolutionsTagArriveReadingsService
  ) {
  }

  ngOnInit() {
    this.resolveSubscription = this.resultsImportService.loadRace$().pipe(
      tap(([racePlan, rankings, drivers$]) => {
        this.racePlan = racePlan;
        this.rankings = rankings;
      }),
      // tap(v => console.log(11, 'Loading race plan with rankings: ', v)),
      switchMap(([racePlan, rankings, drivers$]) => merge(
        drivers$.pipe(
          takeUntil(this.driversFromCsv$)
        ),
        this.driversFromCsv$
      ).pipe(
        // tap(v => console.log(12, 'Loading drivers from Spreadsheet or CSV: ', v)),
        tap(drivers => this.driversService.drivers = drivers),
        tap(drivers => this.drivers$.next(drivers)),
        switchMap(drivers => this.resultsFromTagReadings$),
        // tap(v => console.log(13, 'Loading results from TagReadings: ', v)),
        tap(results => {
          const changed = this.resultsService.mergeToState(results);
          console.log('state: ' + this.resultsService.results.length + ', changed: ' + changed.length);
          if (this.remoteResultsService.isSyncing()) {
            this.remoteResultsService.db.bulkDocs(changed);
          }
        })
      ))
    ).subscribe();
  }

  toggleRemoteRawTagReadingsWatcher(toggle: boolean, fileName: string, intervalSeconds: number = 1) {
    if (!this.remoteRawTagReadingsWatcher$) {
      this.remoteRawTagReadingsWatcher$ = this.resultsImportService.createRemoteRawTagReadingsWatcher$(fileName, 1000 * intervalSeconds);
    }

    if (this.remoteRawTagReadingsWatcherSubscription) {
      this.remoteRawTagReadingsWatcherSubscription.unsubscribe();
    }

    if (toggle) {
      this.remoteRawTagReadingsWatcherSubscription = this.remoteRawTagReadingsWatcher$
        .subscribe((rawTagReadings) => {
          this.updateReadings(rawTagReadings);
        });
    }
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    if (this.resolveSubscription) {
      this.resolveSubscription.unsubscribe();
    }
    if (this.resultSubscription) {
      this.resultSubscription.unsubscribe();
    }
    if (this.syncSubscription) {
      this.syncSubscription.unsubscribe();
    }
    this.resultsService.replaceState([]);
  }

  trackRanking(index: number, ranking: Ranking) {
    return ranking.id;
  }

  updateTagReadings(tagReadings: TagReading[]) {
    this.tagReadings$.next(tagReadings);
  }

  updateReadings(rawContent: string) {
    this.tagReadings$.next(this.parseInput(rawContent));
  }

  parseInput(rawContent: string): TagReading[] {
    const lead = rawContent.slice(0, 200);
    let tagReadings: TagReading[];
    if (this.rfidSolutionsTagArriveReadingsService.isValid(lead)) {
      tagReadings = this.rfidSolutionsTagArriveReadingsService.parseToTagReadings(rawContent);
    } else {
      tagReadings = this.gateReadingsService.parseToTagReadings(rawContent);
    }
    return tagReadings;
  }

  connect(dbName: string) {
    this.dbConnectionSubscription = this.remoteResultsService.connect(dbName).pipe(
      mergeMap(() => this.refreshResults())
    ).subscribe(newResults => {
      console.log('Connected to ' + dbName + '.');
    });
  }

  disconnect() {
    this.dbConnectionSubscription.unsubscribe();
    return this.remoteResultsService.disconnect();
  }

  isConnected() {
    return this.remoteResultsService.isConnected();
  }

  refreshResults() {
    console.log('Refreshing results...');
    return this.remoteResultsService.findAll().pipe(
      tap(results => console.log('Refreshed results', results.length)),
      map(results => this.resultsService.mergeToState(results)),
      tap(changed => console.log('Refreshed changes', changed.length)),
      tap(changed => this.resultsService.broadcastChanges(changed))
    );
  }

  sync(toggle) {
    if (toggle) {
      console.log('Turning sync on...');
    } else {
      console.log('Turning sync off...');
    }
    if (this.syncSubscription) {
      this.syncSubscription.unsubscribe();
    }
    if (toggle) {
      this.syncSubscription = this.remoteResultsService.syncOn(true, true).pipe(
        // takeWhile(state => !state),
        mergeMap(state => toggle ? this.refreshResults() : [[]])
      ).subscribe(newResults => {
        if (toggle) {
          console.log('Sync is now on.');
          console.log('Pulled ' + newResults.length + ' results after sync.');
        } else {
          console.log('Sync is now off.');
        }
      });
    } else {
      this.remoteResultsService.syncOff();
      console.log('Sync is now off.');
    }
  }

  exportTagReadings() {
    this.jsonExport = this.tagReadings;
  }

  exportResults() {
    this.jsonExport = this.resultsService.results;
  }

  clearState() {
    this.resultsService.clearState();
    this.updateTagReadings([]);
  }
}
